import { useCallback, useMemo } from "react";
import { useKeycloakContext } from "@/auth/KeycloakProvider";

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: string[];
  tenantId: number | null;
}

export function useAuth() {
  const { keycloak, initialized } = useKeycloakContext();

  const user = useMemo<AuthUser | null>(() => {
    if (!keycloak.authenticated || !keycloak.tokenParsed) {
      return null;
    }

    const tokenParsed = keycloak.tokenParsed;
    const realmRoles = tokenParsed.realm_access?.roles ?? [];

    return {
      id: tokenParsed.sub ?? "",
      username: tokenParsed.preferred_username ?? "",
      email: tokenParsed.email ?? "",
      firstName: tokenParsed.given_name ?? "",
      lastName: tokenParsed.family_name ?? "",
      fullName: tokenParsed.name ?? "",
      roles: realmRoles,
      tenantId: ((tokenParsed as Record<string, unknown>).tenant_id as number | null) ?? null,
    };
  }, [keycloak.authenticated, keycloak.tokenParsed]);

  const isAdmin = useMemo(() => {
    return user?.roles.includes("admin") ?? false;
  }, [user]);

  const isTenantAdmin = useMemo(() => {
    return user?.roles.includes("tenant_admin") ?? false;
  }, [user]);

  const login = useCallback(() => {
    keycloak.login();
  }, [keycloak]);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, [keycloak]);

  const token = keycloak.token ?? null;

  return {
    initialized,
    authenticated: keycloak.authenticated ?? false,
    user,
    token,
    isAdmin,
    isTenantAdmin,
    login,
    logout,
    keycloak,
  };
}
